import React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';

export interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  available: boolean;
}

interface TimeSlotPickerProps {
  timeSlots: TimeSlot[];
  selectedTimeSlot: TimeSlot | null;
  onSelectTimeSlot: (timeSlot: TimeSlot) => void;
  isLoading?: boolean;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  timeSlots,
  selectedTimeSlot,
  onSelectTimeSlot,
  isLoading = false,
}) => {
  // Parse datetime safely with better error handling
  const parseDateTime = (dateTimeStr: string): Date | null => {
    try {
      // First try to parse as ISO string
      return parseISO(dateTimeStr);
    } catch (e) {
      console.warn(`Failed to parse date: ${dateTimeStr}`, e);
      return null;
    }
  };

  // Group time slots by morning and afternoon
  const morningSlots = timeSlots.filter(slot => {
    const date = parseDateTime(slot.startTime);
    if (!date) return false;
    return date.getHours() < 12;
  });

  const afternoonSlots = timeSlots.filter(slot => {
    const date = parseDateTime(slot.startTime);
    if (!date) return false;
    return date.getHours() >= 12;
  });

  const formatTimeDisplay = (timeString: string) => {
    const date = parseDateTime(timeString);
    if (date) {
      return format(date, 'h:mm a'); // e.g., "1:30 PM"
    } else {
      // Fallback for non-ISO strings
      try {
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours, 10);
        if (isNaN(hour)) return timeString; // Return original if parsing fails
        
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
        return `${displayHour}:${minutes || '00'} ${period}`;
      } catch (e) {
        console.warn(`Could not format time: ${timeString}`, e);
        return timeString; // Return original string if all parsing fails
      }
    }
  };

  const renderTimeSlots = (slots: TimeSlot[]) => {
    if (slots.length === 0) return <div className="text-sm text-muted-foreground">No time slots available</div>;

    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {slots.map((slot) => (
          <Button
            key={slot.id}
            variant={selectedTimeSlot?.id === slot.id ? "default" : "outline"}
            className={cn(
              "w-full justify-center py-2",
              !slot.available && "opacity-50 cursor-not-allowed"
            )}
            disabled={!slot.available}
            onClick={() => slot.available && onSelectTimeSlot(slot)}
          >
            {formatTimeDisplay(slot.startTime)}
          </Button>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-2">
          <div className="h-6 w-24 bg-gray-200 rounded-md"></div>
          <div className="grid grid-cols-3 gap-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 rounded-md"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!timeSlots || timeSlots.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No available time slots for the selected date</p>
        <p className="text-sm text-muted-foreground mt-1">Please select a different date</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {morningSlots.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Morning</h3>
          {renderTimeSlots(morningSlots)}
        </div>
      )}
      
      {afternoonSlots.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Afternoon</h3>
          {renderTimeSlots(afternoonSlots)}
        </div>
      )}

      {morningSlots.length === 0 && afternoonSlots.length === 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No available time slots for the selected date</p>
          <p className="text-sm text-muted-foreground mt-1">Please select a different date</p>
        </div>
      )}
    </div>
  );
};

export default TimeSlotPicker; 